import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { LucideAngularModule, FileDown, X } from 'lucide-angular';

/**
 * TVG-52 / TVG-250: "Create PDF" section picker. Staff tick which of the event's
 * logical sections to include; the chosen keys are emitted and the combined PDF
 * is generated server-side (each section rendered exactly as in the full PDF, in
 * the standard order). "Select all" reproduces the full event PDF. Generating
 * with nothing selected is blocked.
 *
 * The section list mirrors the event page tabs. Sections whose tab is gated by a
 * feature toggle carry a `toggle` key and are hidden here when that toggle is off
 * (Table Plan), matching event-view.component's visibleTabOptions.
 *
 * The section keys and order MUST stay in sync with EventController::PDF_SECTIONS.
 */
export interface PdfBuilderSection {
  key: string;
  label: string;
  /** Feature-toggle key gating this section; omitted when always available. */
  toggle?: string;
}

/**
 * TVG-250: every printable section in canonical order. Toggle-gated entries are
 * filtered out in the component when their toggle is off.
 */
const ALL_PDF_SECTIONS: readonly PdfBuilderSection[] = [
  { key: 'event_information', label: 'Event Information' },
  { key: 'itinerary', label: 'Itinerary' },
  { key: 'financial_summary', label: 'Products & Financial Summary' },
  { key: 'contractual_checks', label: 'Contractual Checks' },
  { key: 'payments', label: 'Payments' },
  { key: 'planner', label: 'Planner' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'guest_dietary', label: 'Guest Dietary & Allergens' },
  { key: 'table_plan', label: 'Table Plan', toggle: 'table-plan-tab' },
  { key: 'accommodation', label: 'Accommodation' },
  { key: 'rules_conduct', label: 'Rules & Conduct' },
  { key: 'notes', label: 'Notes' },
  { key: 'reminders', label: 'Reminders' },
  { key: 'chef_sheet', label: 'Chef Sheet' },
];

@Component({
  selector: 'app-event-pdf-builder-modal',
  standalone: true,
  imports: [CommonModule, ButtonComponent, LucideAngularModule],
  template: `
    @if (isOpen) {
      <div class="fixed inset-0 z-[1000] overflow-y-auto" data-testid="pdf-builder-modal">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50 transition-opacity" (click)="onCancel()"></div>

          <div class="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
            <div class="flex items-start gap-4 p-6 pb-4">
              <div class="flex h-11 w-11 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-brand-900/20 dark:text-brand-400">
                <lucide-icon [name]="FileDownIcon" [size]="22"></lucide-icon>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                  Create PDF
                </h3>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Choose the sections to include. The PDF contains only the ticked sections, in the standard order.
                </p>
              </div>
              <button
                class="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                (click)="onCancel()"
                aria-label="Close Create PDF">
                <lucide-icon [name]="XIcon" [size]="18"></lucide-icon>
              </button>
            </div>

            <div class="px-6">
              <label
                class="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-700"
                data-testid="pdf-section-select-all">
                <input
                  type="checkbox"
                  class="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  [checked]="allSelected"
                  (change)="toggleAll($any($event.target).checked)" />
                <span class="text-sm font-semibold text-gray-900 dark:text-white">Select all</span>
              </label>

              <div class="mt-2 max-h-72 space-y-1 overflow-y-auto py-1">
                @for (section of sections; track section.key) {
                  <label
                    class="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800"
                    [attr.data-testid]="'pdf-section-' + section.key">
                    <input
                      type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      [checked]="selected[section.key]"
                      (change)="toggle(section.key, $any($event.target).checked)" />
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ section.label }}</span>
                  </label>
                }
              </div>

              @if (selectedCount === 0) {
                <p class="mt-1 text-sm text-error-600 dark:text-error-400" data-testid="pdf-section-empty-hint">
                  Pick at least one section to generate a PDF.
                </p>
              }
            </div>

            <div class="mt-4 flex items-center justify-end gap-3 bg-gray-50 px-6 py-4 dark:bg-gray-800/60">
              <app-button
                [label]="'Cancel'"
                [style]="'secondary'"
                [icon]="XIcon"
                (click)="onCancel()">
              </app-button>
              <app-button
                [label]="submitting ? 'Generating…' : 'Generate PDF'"
                [style]="'primary'"
                [icon]="FileDownIcon"
                [disabled]="submitting || selectedCount === 0"
                data-testid="pdf-builder-submit"
                (click)="onConfirm()">
              </app-button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
})
export class EventPdfBuilderModalComponent {
  @Input() isOpen = false;
  @Input() submitting = false;

  // TVG-250: only the sections whose tab is currently visible (toggle-gated
  // ones drop out when their toggle is off), keeping the picker in step with
  // the event page tabs.
  protected readonly sections: PdfBuilderSection[];

  // Default to everything ticked so the initial state reproduces the full PDF.
  protected selected: Record<string, boolean>;

  @Output() confirmed = new EventEmitter<string[]>();
  @Output() cancelled = new EventEmitter<void>();

  protected readonly FileDownIcon = FileDown;
  protected readonly XIcon = X;

  constructor(private readonly featureToggleService: FeatureToggleService) {
    this.sections = ALL_PDF_SECTIONS.filter(
      (section) => !section.toggle || this.featureToggleService.isEnabled(section.toggle),
    );
    this.selected = this.sections.reduce(
      (acc, section) => ({ ...acc, [section.key]: true }),
      {} as Record<string, boolean>,
    );
  }

  protected get selectedCount(): number {
    return this.sections.filter((section) => this.selected[section.key]).length;
  }

  protected get allSelected(): boolean {
    return this.selectedCount === this.sections.length;
  }

  protected toggle(key: string, checked: boolean): void {
    this.selected = { ...this.selected, [key]: checked };
  }

  protected toggleAll(checked: boolean): void {
    this.selected = this.sections.reduce(
      (acc, section) => ({ ...acc, [section.key]: checked }),
      {} as Record<string, boolean>,
    );
  }

  protected onConfirm(): void {
    if (this.submitting || this.selectedCount === 0) {
      return;
    }
    const chosen = this.sections
      .filter((section) => this.selected[section.key])
      .map((section) => section.key);
    this.confirmed.emit(chosen);
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }
}
